import { useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import Layout from '@/components/Layout';
import { getCompanyBySlug, companyPath, ROUTE_PATHS } from '@/lib/index';

function FadeUp({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 32 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9, delay, ease: [0.25, 0.46, 0.45, 0.94] }} className={className}>
      {children}
    </motion.div>
  );
}

export default function CompanyServices() {
  const { slug } = useParams<{ slug: string }>();
  const company = getCompanyBySlug(slug ?? '');
  if (!company) return <Layout><div className="min-h-screen flex items-center justify-center"><Link to={ROUTE_PATHS.COMPANIES} className="btn-gold-outline">Back</Link></div></Layout>;

  return (
    <Layout activeCompanySlug={company.slug}>
      {/* Hero */}
      <section className="relative pt-40 pb-20 min-h-[50vh] flex items-center overflow-hidden bg-[#080808]">
        <div className="absolute inset-0">
          <img src={company.heroImage} alt={company.name} className="w-full h-full object-cover opacity-15" />
          <div className="absolute inset-0 bg-gradient-to-b from-[rgba(8,8,8,0.5)] to-[#080808]" />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }}>
            <p className="eyebrow mb-4">{company.name}</p>
            <h1 className="heading-display text-[clamp(3rem,7vw,5.5rem)] text-[#f5f5f5] max-w-3xl">
              Our <span className="text-gold italic">Services</span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Services detailed grid */}
      <section className="py-20 bg-[#080808]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[rgba(201,168,76,0.08)]">
            {company.services.map((service, i) => (
              <FadeUp key={i} delay={i * 0.1}>
                <div className="bg-[#090909] p-10 lg:p-14 group hover:bg-[rgba(201,168,76,0.025)] transition-colors duration-400 h-full">
                  <div className="flex items-start gap-5">
                    <span className="heading-display text-5xl text-gold opacity-15 leading-none mt-1 flex-shrink-0">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <div>
                      <h3 className="heading-display text-2xl md:text-3xl text-[#f5f5f5] mb-4">{service.title}</h3>
                      <div className="divider-gold-left" />
                      <p className="text-[#f5f5f5]/50 text-sm leading-relaxed mt-5">{service.description}</p>
                    </div>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-[#060606]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <FadeUp className="text-center mb-14">
            <p className="eyebrow mb-4">Our Process</p>
            <h2 className="heading-display text-3xl text-[#f5f5f5]">How We Work</h2>
            <div className="divider-gold mt-5" />
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-0">
            {[
              { step: '01', title: 'Discovery', desc: 'We listen, learn, and understand your unique goals, context, and vision.' },
              { step: '02', title: 'Strategy', desc: 'We craft a tailored roadmap aligned with your objectives and timeline.' },
              { step: '03', title: 'Execution', desc: 'Our expert team executes with precision, creativity, and world-class standards.' },
              { step: '04', title: 'Excellence', desc: 'We measure results, refine our approach, and continuously elevate the outcome.' },
            ].map((p, i) => (
              <FadeUp key={i} delay={i * 0.1}>
                <div className="relative p-8 lg:p-10 border-r border-[rgba(201,168,76,0.08)] last:border-r-0">
                  {i < 3 && (
                    <div className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 w-4 h-px bg-[rgba(201,168,76,0.4)]" />
                  )}
                  <span className="heading-display text-4xl text-gold opacity-15 block mb-4">{p.step}</span>
                  <h4 className="heading-display text-xl text-[#f5f5f5] mb-3">{p.title}</h4>
                  <p className="text-[#f5f5f5]/45 text-sm leading-relaxed">{p.desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#080808] text-center">
        <FadeUp>
          <p className="eyebrow mb-5">Get Started</p>
          <h2 className="heading-display text-[clamp(2rem,4vw,3.5rem)] text-[#f5f5f5] mb-8">
            Ready to Begin?
          </h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to={companyPath(company.slug) + '/contact'} className="btn-gold-fill">Contact Us</Link>
            <Link to={companyPath(company.slug) + '/portfolio'} className="btn-gold-outline">View Our Work</Link>
          </div>
        </FadeUp>
      </section>
    </Layout>
  );
}
